// The three steps of getting a quote, shown the same way on the selection
// screen and on the draft: who is asked, what goes with the request, the email.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/QuoteSteps.tsx

import React from 'react';
import { Box, Stack, useTheme } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

export const QUOTE_STEPS = ['Suppliers', 'Lines and files', 'Preview and send'] as const;

interface QuoteStepsProps {
  /** 1-based step the buyer is on. */
  current: 1 | 2 | 3;
  /** Shown after a step's name, for example the number of suppliers chosen. */
  notes?: Partial<Record<1 | 2 | 3, string>>;
  /** Omitted for a step the buyer cannot go to from here. */
  onGo?: Partial<Record<1 | 2 | 3, () => void>>;
}

const QuoteSteps: React.FC<QuoteStepsProps> = ({ current, notes, onGo }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" sx={{ gap: 3, flexWrap: 'wrap' }}>
      {QUOTE_STEPS.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const isCurrent = current === n;
        const done = current > n;
        const go = onGo?.[n];
        return (
          <Box
            key={label}
            component="button"
            type="button"
            disabled={!go}
            aria-current={isCurrent ? 'step' : undefined}
            onClick={go}
            sx={{ font: 'inherit', border: 0, bgcolor: 'transparent', p: 0, display: 'inline-flex', alignItems: 'center', gap: 1, cursor: go ? 'pointer' : 'default', color: isCurrent ? 'text.primary' : 'text.secondary', fontWeight: 600, fontSize: '0.85rem' }}
          >
            <Box
              component="span"
              sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: '0.72rem', border: `1.5px solid ${isCurrent || done ? theme.palette.primary.main : theme.palette.divider}`, bgcolor: isCurrent ? 'primary.main' : 'transparent', color: isCurrent ? 'primary.contrastText' : done ? 'primary.main' : 'text.secondary' }}
            >
              {done ? <CheckIcon sx={{ fontSize: 14 }} /> : n}
            </Box>
            {label}
            {notes?.[n] ? ` · ${notes[n]}` : ''}
          </Box>
        );
      })}
    </Stack>
  );
};

export default QuoteSteps;
